import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Icon, IconButton, Input, useToast } from "@agentsaas/ui";
import { ApiError, type HumanQuestion } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatTime } from "../app/format";
import { useAgents } from "../data/queries";
import { removeQuestion, useOpenQuestions } from "../data/collab";
import { useAsset } from "../data/assets";
import { AssetRefList, assetRefsIn } from "../assets/AssetRef";
import { AssetThumb } from "../assets/AssetThumb";
import { stripAssetRefs } from "../assets/refs";
import { parseAssetRef } from "../rooms/model";

/** agent 的 ask_human：待回答的问题（紧急的排前面） */
export function useQuestions(pid: string) {
  const q = useOpenQuestions(pid);
  const agents = useAgents(pid);
  const nameOf = (aid: string) => agents.data?.find((a) => a.id === aid)?.name ?? aid;
  const items = [...(q.data ?? [])].sort((a, b) => Number(b.urgent) - Number(a.urgent) || a.createdAt.localeCompare(b.createdAt));
  return { items, loading: q.isPending, nameOf };
}

/** 问题正文去掉资产引用（引用在下面预览成卡片） */
export const questionText = stripAssetRefs;

/**
 * 问题卡：有选项时显示选项按钮（一键回答），也可以自由回答。
 * P8：问题里的资产引用预览成卡片（模型可弹窗看 3D）；选项本身是资产引用时渲染成带缩略图的按钮（挑概念图、确认模型）。
 * inline：嵌在 agent 对话里（ask_human 工具调用处）。
 */
export function QuestionCard({ question, agentName, pid, inline }: { question: HumanQuestion; agentName: string; pid: string; inline?: boolean }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const tz = useSession().state.user?.timezone;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const answer = async (value: string) => {
    if (!value.trim()) return;
    setBusy(value);
    try {
      await api.call("questionAnswer", { params: { qid: question.id }, body: { answer: value.trim() } });
      toast.show({ message: t("question.answered", { agent: agentName }), tone: "success", duration: 2000 });
      removeQuestion(qc, question.projectId, question.id);
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
      if (e instanceof ApiError && e.code === "question.already_answered") removeQuestion(qc, question.projectId, question.id);
    } finally {
      setBusy(null);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void answer(text);
  };
  const assetOptions = question.options.filter((o) => parseAssetRef(o.trim()));
  const textOptions = question.options.filter((o) => !parseAssetRef(o.trim()));

  return (
    <article className={`question${question.urgent ? " question--urgent" : ""}${inline ? " question--inline" : ""}`} aria-label={t("question.title", { agent: agentName })} data-testid="question-card" data-qid={question.id}>
      <header className="question__head">
        <Icon name="help" />
        <strong className="grow">{t("question.title", { agent: agentName })}</strong>
        {question.urgent ? <Badge tone="danger">{t("question.urgent")}</Badge> : null}
        <Badge>
          <time dateTime={question.createdAt}>{formatTime(question.createdAt, locale, tz)}</time>
        </Badge>
      </header>
      <p className="question__text">{questionText(question.question)}</p>
      <div className="question__assets">
        <AssetRefList pid={pid} refs={assetRefsIn(question.question)} />
      </div>
      {assetOptions.length ? (
        <div className="question__options question__options--assets" role="group" aria-label={t("question.options")}>
          {assetOptions.map((o) => (
            <AssetOption key={o} value={o} busy={busy === o} disabled={!!busy} onPick={() => void answer(o)} />
          ))}
        </div>
      ) : null}
      {textOptions.length ? (
        <div className="question__options" role="group" aria-label={t("question.options")}>
          {textOptions.map((o) => (
            <Button key={o} variant="secondary" loading={busy === o} disabled={!!busy} onClick={() => void answer(o)} data-testid="question-option">
              {o}
            </Button>
          ))}
        </div>
      ) : null}
      <form className="question__form" onSubmit={submit}>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label={question.options.length ? t("question.other") : t("question.answer")}
          placeholder={question.options.length ? t("question.other") : t("question.answer")}
          enterKeyHint="send"
          data-testid="question-input"
        />
        <IconButton type="submit" icon="send" variant="primary" label={t("question.send")} disabled={!!busy || !text.trim()} data-testid="question-send" />
      </form>
      <Link to={`/p/${pid}/a/${question.agentId}`} className="small" data-close-drawer>
        {t("approval.open_agent")}
      </Link>
    </article>
  );
}

/** 资产选项：缩略图 + 名字的按钮，点击即以该引用作答 */
function AssetOption({ value, busy, disabled, onPick }: { value: string; busy: boolean; disabled: boolean; onPick(): void }) {
  const { t } = useT();
  const ref = parseAssetRef(value.trim())!;
  const q = useAsset(ref.assetId);
  const a = q.data;
  const version = ref.version ?? a?.latestVersion ?? 1;
  const v = a?.versions.find((x) => x.version === version);
  const name = a ? `${a.name} ${t("asset.version_short", { version })}` : value;
  return (
    <button type="button" className="qopt" disabled={disabled} onClick={onPick} aria-label={t("question.pick_asset", { name })} aria-busy={busy} data-testid="question-option" data-option={value}>
      {a ? <AssetThumb asset={a} version={version} hasThumb={v?.hasThumb} /> : <span className="asset-thumb asset-thumb--icon" aria-hidden="true" />}
      <span className="qopt__label small ellipsis">{name}</span>
    </button>
  );
}

export function QuestionList({ pid, items, nameOf }: { pid: string; items: HumanQuestion[]; nameOf(aid: string): string }) {
  const { t } = useT();
  if (!items.length) return <EmptyState icon="help" title={t("question.empty")} />;
  return (
    <div className="stack" data-testid="question-list">
      {items.map((q) => (
        <QuestionCard key={q.id} question={q} agentName={nameOf(q.agentId)} pid={pid} />
      ))}
    </div>
  );
}

/** 顶栏：agent 提问角标 + 列表（手机上自动为底部 sheet） */
export function QuestionBell({ pid }: { pid: string }) {
  const { t } = useT();
  const { items, nameOf } = useQuestions(pid);
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton
        icon="help"
        label={t("question.pending_count", { count: items.length })}
        badge={items.length}
        className={items.some((q) => q.urgent) ? "bell--urgent" : undefined}
        onClick={() => setOpen(true)}
        data-testid="question-bell"
      />
      <Dialog open={open} onClose={() => setOpen(false)} title={t("question.list_title")} closeLabel={t("shell.close")}>
        <div onClick={(e) => (e.target as HTMLElement).closest("a") && setOpen(false)}>
          <QuestionList pid={pid} items={items} nameOf={nameOf} />
        </div>
      </Dialog>
    </>
  );
}
